/**
 * Link — Floating Surface design system.
 *
 * Every pill, card, drawer, and floating chrome element renders above
 * the live map. To make the product feel layered + physical (Uber /
 * Apple Maps / Lyft territory) every floating surface must share the
 * same elevation language: layered ambient + key shadows, soft
 * backdrop blur, hairline border, and a subtle ambient brand glow.
 *
 * THREE TIERS — pick the one that matches the surface's role:
 *
 *   chip    — small floating affordances (top pill, bottom tab bar,
 *             trip chip, calc preview). Sits ~8px above the map.
 *   card    — medium hovering cards (booking sheet pill, install /
 *             location prompts, driver marketplace pill). ~16px up.
 *   drawer  — large full-height sheets (Account drawer, sector
 *             detail, ride detail). ~24px up.
 *
 * Each tier provides:
 *   - `shadow`    A layered ambient + key + optional ground shadow.
 *                 Use inline: `style={{ boxShadow: SURFACE.card.shadow }}`
 *   - `glow`      Subtle warm-orange ambient halo so the surface feels
 *                 lit from the brand color, not just gray-on-gray.
 *   - `blur`      Backdrop-filter blur strength in px.
 *   - `border`    Hairline border color (light + dark variants).
 *   - `radius`    Default radius (override per-surface if needed).
 *
 * The CSS-class helper `surfaceClass(tier, { theme })` returns the
 * Tailwind class string for transitions + blur + border — drop it
 * into any element's className and spread `surfaceStyle(tier, ...)`
 * onto its style prop to get the shadow + glow stack.
 */

const SHADOW = {
  // Pieces composed below into the actual `boxShadow` strings.
  hairline:   "0 0 0 1px rgba(10, 17, 40, 0.04)",
  ambientLow: "0 1px 2px rgba(10, 17, 40, 0.06), 0 2px 6px rgba(10, 17, 40, 0.07)",
  ambientMid: "0 2px 4px rgba(10, 17, 40, 0.06), 0 6px 14px rgba(10, 17, 40, 0.08)",
  ambientHi:  "0 4px 8px rgba(10, 17, 40, 0.08), 0 10px 24px rgba(10, 17, 40, 0.10)",

  keyChip:    "0 10px 28px rgba(10, 17, 40, 0.14)",
  keyCard:    "0 18px 44px rgba(10, 17, 40, 0.18)",
  keyDrawer:  "0 28px 64px rgba(10, 17, 40, 0.22)",

  // Deep ground shadow only on the drawer tier so it reads as the
  // "heaviest" surface in the layout.
  groundDrawer: "0 48px 120px rgba(10, 17, 40, 0.16)",

  // Subtle warm ambient glow — uses the Link orange very gently so
  // the surface feels lit by the brand, not bathed in it.
  glowChip:   "0 0 24px rgba(237, 101, 0, 0.06)",
  glowCard:   "0 0 36px rgba(237, 101, 0, 0.08)",
  glowDrawer: "0 0 48px rgba(237, 101, 0, 0.06)",
};

const compose = (...parts) => parts.filter(Boolean).join(", ");

export const SURFACE = {
  chip: {
    shadow: compose(SHADOW.hairline, SHADOW.ambientLow, SHADOW.keyChip, SHADOW.glowChip),
    blur: 20,
    radius: 999,
    borderLight: "rgba(255, 255, 255, 0.45)",
    borderDark:  "rgba(255, 255, 255, 0.10)",
  },
  card: {
    shadow: compose(SHADOW.hairline, SHADOW.ambientMid, SHADOW.keyCard, SHADOW.glowCard),
    blur: 24,
    radius: 24,
    borderLight: "rgba(255, 255, 255, 0.45)",
    borderDark:  "rgba(255, 255, 255, 0.10)",
  },
  drawer: {
    shadow: compose(SHADOW.hairline, SHADOW.ambientHi, SHADOW.keyDrawer, SHADOW.groundDrawer, SHADOW.glowDrawer),
    blur: 28,
    radius: 32,
    borderLight: "rgba(255, 255, 255, 0.45)",
    borderDark:  "rgba(255, 255, 255, 0.10)",
  },
  // Inverted (dark navy) surfaces — same elevation but the glow tone
  // shifts cooler so the orange doesn't muddy the dark backdrop.
  chipDark: {
    shadow: compose(SHADOW.ambientLow, SHADOW.keyChip, "0 0 24px rgba(92, 151, 247, 0.08)"),
    blur: 20,
    radius: 999,
    borderLight: "rgba(255, 255, 255, 0.10)",
    borderDark:  "rgba(255, 255, 255, 0.10)",
  },
  cardDark: {
    shadow: compose(SHADOW.ambientMid, SHADOW.keyCard, "0 0 36px rgba(92, 151, 247, 0.10)"),
    blur: 24,
    radius: 24,
    borderLight: "rgba(255, 255, 255, 0.10)",
    borderDark:  "rgba(255, 255, 255, 0.10)",
  },
  drawerDark: {
    shadow: compose(SHADOW.ambientHi, SHADOW.keyDrawer, SHADOW.groundDrawer, "0 0 48px rgba(92, 151, 247, 0.10)"),
    blur: 28,
    radius: 32,
    borderLight: "rgba(255, 255, 255, 0.10)",
    borderDark:  "rgba(255, 255, 255, 0.10)",
  },
};

/**
 * Returns an inline `style` object for a surface tier. Spread it onto
 * the surface's `style` prop:
 *
 *   <div style={{ ...surfaceStyle("chip"), ...localStyle }}>
 *
 * `theme` defaults to "light"; pass "dark" for inverted surfaces
 * (e.g. the dark navy MobileTabBar). The shadow stack stays the same;
 * the border tone flips so it remains a soft 1px line on either bg.
 */
export function surfaceStyle(tier = "card", { theme = "light" } = {}) {
  const t = theme === "dark" ? `${tier}Dark` : tier;
  const def = SURFACE[t] || SURFACE.card;
  return {
    boxShadow: def.shadow,
    WebkitBackdropFilter: `blur(${def.blur}px) saturate(140%)`,
    backdropFilter: `blur(${def.blur}px) saturate(140%)`,
    border: `1px solid ${theme === "dark" ? def.borderDark : def.borderLight}`,
  };
}

/**
 * Tailwind classes shared by every floating surface — handles smooth
 * transitions so shadow / transform shifts feel like the same family.
 * The actual shadow + blur come from `surfaceStyle()`; keep these two
 * helpers paired.
 */
export function surfaceClass() {
  // `will-change: transform` so the GPU keeps the backdrop blur cached
  // during the spring; `transition-shadow` smooths hover/focus tone
  // shifts. We deliberately omit `transition: all` (breaks transforms).
  return "will-change-transform transition-[box-shadow,transform] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]";
}

/**
 * Spring presets for surface motion. The whole product uses the same
 * three so a drawer feels like the same physics family as a chip pop.
 */
export const SURFACE_SPRING = {
  chip:   { type: "spring", stiffness: 520, damping: 38, mass: 0.6 },
  card:   { type: "spring", stiffness: 420, damping: 36, mass: 0.8 },
  drawer: { type: "spring", stiffness: 360, damping: 34, mass: 1.0 },
};
